import { z } from 'zod';
import { CrmPartnerClientSchema } from './crmPartner';
import { CrmRole } from './crmRole';

/**
 * Linha da tela oficial **Clientes** do CRM (`/accounts`): um tenant do app
 * no escopo de quem pergunta.
 *
 * Origem: #775 — 2026-09-29 09:40 +0200 (F3 do épico #772).
 *
 * Por quê: até aqui o CRM só tinha "Clientes / Onboarding", que lista LEADS
 * ganhos; e o parceiro tinha uma lista própria de tenants
 * (`CrmPartnerClientSchema`, `/partner/clients`). O owner decidiu (2026-09-29)
 * separar Onboarding (leads) de Clientes (tenants) e acabar com a tela do
 * parceiro — então a linha de Clientes é a do parceiro **estendida** com o que
 * o time interno precisa (documento, país, plano, parceiro, vendedor). Estender
 * em vez de copiar mantém uma regra só para "Cobrado" e "Repasse / comissão"
 * (`partnerClientAmounts`).
 *
 * Contrato: campos herdados — ver `CrmPartnerClientSchema`
 * (`packages/types/src/crmPartner.ts`). `model`/`commissionPercent`/
 * `lastCommissionCents`/`totalRemitCents` só têm sentido quando `partnerId`
 * não é nulo; para cliente sem parceiro a tela mostra "—".
 *
 * Usado por: callable `crmListClients` (`apps/crm-functions/src/crmClientsList.ts`)
 * → CRM web `CustomersPage` (`/accounts`) e a aba Clientes do editor de
 * parceiro (`/admin/partners/:partnerId`).
 *
 * Issues: #772 (épico), #775 (esta fase), #743 (parceiro).
 */
export const CrmClientRowSchema = CrmPartnerClientSchema.extend({
  /** Documento (CNPJ/NIF…) como gravado no tenant; `null` se ausente. */
  taxId: z.string().nullable(),
  /** País do tenant (`tenant.countryCode`), ex.: `BR`. */
  countryCode: z.string().nullable(),
  /** `billing/subscription.plan_id` (legado de plano único); `null` sem assinatura. */
  planId: z.string().nullable(),
  /** Parceiro de revenda do tenant (`tenant.partner_id`) e o nome atual dele. */
  partnerId: z.string().nullable(),
  partnerName: z.string().nullable(),
  /** Vendedor de origem (`tenant.sales_origin.seller_uid`) e o nome atual. */
  sellerUserId: z.string().nullable(),
  sellerName: z.string().nullable(),
});
export type CrmClientRow = z.infer<typeof CrmClientRowSchema>;

/**
 * Entrada de `crmListClients`. `partnerId` é filtro do ADMIN/AUDITOR (a aba
 * Clientes do editor de parceiro); para os demais papéis é ignorado — o
 * escopo vem do servidor.
 */
export const CrmListClientsInputSchema = z.object({
  partnerId: z.string().min(1).max(128).optional(),
});
export type CrmListClientsInput = z.infer<typeof CrmListClientsInputSchema>;

export const CrmListClientsOutputSchema = z.object({
  clients: z.array(CrmClientRowSchema),
});
export type CrmListClientsOutput = z.infer<typeof CrmListClientsOutputSchema>;

/** Nível de acesso de um usuário do CRM a um tenant (tela do cliente). */
export const CrmTenantAccessLevel = {
  NONE: 'NONE',
  READ: 'READ',
  WRITE: 'WRITE',
} as const;
export type CrmTenantAccessLevel = (typeof CrmTenantAccessLevel)[keyof typeof CrmTenantAccessLevel];

/** Lead de origem de um tenant, reduzido ao que decide o acesso. */
export type CrmTenantOriginLead = {
  readonly ownerUserId: string;
  /** Gerente DO LEAD (`lead.managerUserId`). */
  readonly managerUserId?: string | null;
  /** Gerente do QUADRO do lead (`crm_boards/{b}.managerUserId`). */
  readonly boardManagerUserId?: string | null;
  /** Gerente DIRETO do dono do lead (`crm_users/{owner}.managerUserId`). */
  readonly ownerDirectManagerUserId?: string | null;
};

/**
 * Quanto um usuário do CRM pode fazer com um tenant na tela do cliente
 * (`/accounts/:tenantId`). Puro — a mesma regra vale no servidor que lê
 * (`crmGetClientTab`) e no que muda (callables do admin abertas ao CRM).
 *
 * Origem: #776 — 2026-09-29 09:40 +0200 (F4 do épico #772).
 *
 * Por quê: o owner decidiu (2026-09-29) que a tela do cliente tem as MESMAS
 * ações do admin para todo usuário que vê o cliente; o AUDITOR, papel de
 * leitura, só lê. "Ver o cliente" é o mesmo escopo da lista Clientes
 * (`clientListScope` em `apps/crm-functions/src/lib/clientRows.ts`):
 *  - ADMIN: todos, com escrita. AUDITOR: todos, só leitura.
 *  - PARTNER: tenants da empresa dele (`tenant.partner_id`), com escrita.
 *  - SELLER: tenants cujo lead de origem é DELE.
 *  - MANAGER: os quatro conjuntos do `leadReadScope` — lead próprio, lead de
 *    que é gerente, lead de quadro que gerencia, lead de alguém da equipe
 *    direta dele.
 *
 * Contrato: `originLeads` é o lead de ORIGEM do tenant — o que
 * `tenant.sales_origin` aponta, já vinculado a ele (zero ou um; desde a #795,
 * CRM-1, 2026-10-03, nunca "todo lead com `tenantId == tenant`", porque esse
 * campo nasce do CNPJ digitado pelo vendedor). Sem lead de origem, só
 * ADMIN/AUDITOR/parceiro do tenant acessam.
 * Parceiro sem `partnerId` ou tenant sem `partner_id` → `NONE`.
 *
 * Usado por: `resolveCrmTenantAccess` (`apps/functions/src/lib/crmTenantAccess.ts`).
 *
 * Issues: #772, #776, #795 (CRM-1: quem monta `originLeads`).
 */
export function crmTenantAccessLevel(args: {
  readonly role: string;
  readonly uid: string;
  readonly userPartnerId: string | null | undefined;
  readonly tenantPartnerId: string | null | undefined;
  readonly originLeads: readonly CrmTenantOriginLead[];
}): CrmTenantAccessLevel {
  const { role, uid } = args;
  if (role === CrmRole.ADMIN) return CrmTenantAccessLevel.WRITE;
  if (role === CrmRole.AUDITOR) return CrmTenantAccessLevel.READ;
  if (role === CrmRole.PARTNER) {
    return args.userPartnerId && args.tenantPartnerId === args.userPartnerId
      ? CrmTenantAccessLevel.WRITE
      : CrmTenantAccessLevel.NONE;
  }
  const sees = (l: CrmTenantOriginLead): boolean => {
    if (l.ownerUserId === uid) return true;
    if (role !== CrmRole.MANAGER) return false;
    return l.managerUserId === uid || l.boardManagerUserId === uid || l.ownerDirectManagerUserId === uid;
  };
  if (role === CrmRole.SELLER || role === CrmRole.MANAGER) {
    return args.originLeads.some(sees) ? CrmTenantAccessLevel.WRITE : CrmTenantAccessLevel.NONE;
  }
  return CrmTenantAccessLevel.NONE;
}

/**
 * Abas da tela do cliente do CRM (`/accounts/:tenantId`) — as mesmas do
 * admin (#776, owner 2026-09-29), mais `summary` (cabeçalho + Assinatura).
 * Uma constante por conceito, nunca literal solto.
 */
export const CrmClientTab = {
  SUMMARY: 'summary',
  FINANCE: 'finance',
  USAGE: 'usage',
  PRODUCTS: 'products',
  TEMPLATES: 'templates',
  PRINTERS: 'printers',
  MEMBERS: 'members',
  RESPONSIBLES: 'responsibles',
  AUDIT: 'audit',
} as const;
export type CrmClientTab = (typeof CrmClientTab)[keyof typeof CrmClientTab];
export const CrmClientTabSchema = z.enum(Object.values(CrmClientTab) as [CrmClientTab, ...CrmClientTab[]]);

/**
 * Entrada de `crmGetClientTab`: o tenant e a aba.
 *
 * Origem: #776 — 2026-09-29 10:30 +0200.
 */
export const CrmGetClientTabInputSchema = z.object({
  // Sem `/`: `doc(tenantId)` com barra resolve para um SUBdocumento
  // (auditoria 2026-09-29 12:10 +0200, #776, achado 4).
  tenantId: z.string().min(1).max(128).regex(/^[^/]+$/),
  tab: CrmClientTabSchema,
});
export type CrmGetClientTabInput = z.infer<typeof CrmGetClientTabInputSchema>;

/**
 * Saída de `crmGetClientTab`: coleções da aba, com os documentos já
 * normalizados (Timestamp → millis) mas **não** validados — o cliente valida
 * cada um com o schema do domínio (`TenantSchema`, `InvoiceSchema`…), como o
 * admin faz nas leituras diretas, e reporta o que descartar.
 *
 * `canWrite`: o nível de acesso do chamador (AUDITOR = `false`) — a tela
 * esconde as ações; o servidor revalida em cada callable.
 */
export const CrmGetClientTabOutputSchema = z.object({
  tab: CrmClientTabSchema,
  canWrite: z.boolean(),
  data: z.record(z.string(), z.unknown()),
});
export type CrmGetClientTabOutput = z.infer<typeof CrmGetClientTabOutputSchema>;
